import React, { useEffect, useRef } from 'react';
import { ShieldCheck, Gauge, Leaf, Heart } from 'lucide-react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

type FeatureProps = {
  icon: React.ReactNode;
  title: string;
  description: string;
  index: number;
};

const Feature: React.FC<FeatureProps> = ({ icon, title, description, index }) => {
  const featureRef = useRef<HTMLDivElement>(null);
  const iconRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const descRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    const element = featureRef.current;
    const iconElement = iconRef.current;
    const titleElement = titleRef.current;
    const descElement = descRef.current;

    if (!element) return;

    // Initial state
    gsap.set(element, {
      opacity: 0,
      y: 80,
      scale: 0.8,
      rotationY: -15
    });

    gsap.set(iconElement, {
      scale: 0,
      rotation: -180
    });

    gsap.set([titleElement, descElement], {
      opacity: 0,
      y: 20
    });

    // Scroll-triggered animation
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: element,
        start: "top 85%",
        end: "bottom 15%",
        toggleActions: "play none none reverse"
      }
    });

    // Animate the card
    tl.to(element, {
      opacity: 1,
      y: 0,
      scale: 1,
      rotationY: 0,
      duration: 0.8,
      ease: "back.out(1.7)",
      delay: index * 0.15
    });

    // Animate the icon
    tl.to(iconElement, {
      scale: 1,
      rotation: 0,
      duration: 0.6,
      ease: "back.out(1.7)"
    }, "-=0.4");

    // Animate text elements
    tl.to(titleElement, {
      opacity: 1,
      y: 0,
      duration: 0.5,
      ease: "power2.out"
    }, "-=0.3");

    tl.to(descElement, {
      opacity: 1,
      y: 0,
      duration: 0.5,
      ease: "power2.out"
    }, "-=0.2");

    // Add hover animations
    const handleMouseEnter = () => {
      gsap.to(element, {
        y: -15,
        scale: 1.05,
        rotationY: 5,
        duration: 0.4,
        ease: "power2.out"
      });
      
      gsap.to(iconElement, {
        scale: 1.2,
        rotation: 15,
        duration: 0.4,
        ease: "power2.out"
      });

      // Add glow effect
      gsap.to(element, {
        boxShadow: "0 20px 40px rgba(204, 0, 0, 0.15)",
        duration: 0.4,
        ease: "power2.out"
      });
    };

    const handleMouseLeave = () => {
      gsap.to(element, {
        y: 0,
        scale: 1,
        rotationY: 0,
        duration: 0.4,
        ease: "power2.out"
      });
      
      gsap.to(iconElement, {
        scale: 1,
        rotation: 0,
        duration: 0.4,
        ease: "power2.out"
      });

      // Remove glow effect
      gsap.to(element, {
        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
        duration: 0.4,
        ease: "power2.out"
      });
    };

    element.addEventListener('mouseenter', handleMouseEnter);
    element.addEventListener('mouseleave', handleMouseLeave);

    // Add floating animation
    gsap.to(element, {
      y: -3,
      duration: 3,
      ease: "power1.inOut",
      yoyo: true,
      repeat: -1,
      delay: index * 0.5
    });

    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
      element.removeEventListener('mouseenter', handleMouseEnter);
      element.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [index]);

  return (
    <div 
      ref={featureRef} 
      className="bg-[#F5F5F5] p-8 rounded-lg cursor-pointer transform-gpu"
      style={{ 
        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
        perspective: "1000px"
      }}
    >
      <div 
        ref={iconRef}
        className="text-[#CC0000] mb-4 inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#FFEBEB]"
      >
        {icon}
      </div>
      <h3 ref={titleRef} className="text-xl font-semibold text-[#1A1A1A] mb-3">{title}</h3>
      <p ref={descRef} className="text-gray-600">{description}</p>
    </div>
  );
};

const Features: React.FC = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    const title = titleRef.current;

    if (!section || !title) return;

    // Initial state
    gsap.set(title, {
      opacity: 0,
      y: 50,
      scale: 0.9
    });

    // Title animation
    gsap.to(title, {
      opacity: 1,
      y: 0,
      scale: 1,
      duration: 1,
      ease: "back.out(1.7)",
      scrollTrigger: {
        trigger: section,
        start: "top 80%",
        end: "bottom 20%",
        toggleActions: "play none none reverse"
      }
    });

    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  const features = [
    {
      icon: <ShieldCheck size={24} />,
      title: "Uncompromising Safety",
      description: "Advanced safety systems and robust build quality ensuring maximum protection for drivers and passengers in all operating conditions.",
    },
    {
      icon: <Gauge size={24} />,
      title: "Superior Performance",
      description: "High-efficiency electric motors delivering instant torque, smooth acceleration, and reliable performance for last mile mobility needs.",
    },
    {
      icon: <Leaf size={24} />,
      title: "Zero Emissions",
      description: "100% electric powertrain contributing to cleaner air and sustainable last mile transportation while reducing operational costs.",
    },
    {
      icon: <Heart size={24} />,
      title: "Premium Care",
      description: "Comprehensive after-sales support with 24/7 service assistance, regular maintenance programs, and dedicated customer care for complete peace of mind.",
    }
  ];

  return (
    <section ref={sectionRef} id="vehicles" className="py-20 bg-white overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <h2 ref={titleRef} className="text-3xl font-bold text-center text-[#1A1A1A] mb-12">Why Choose Dawki Mobility</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {features.map((feature, index) => (
            <Feature 
              key={index} 
              icon={feature.icon} 
              title={feature.title} 
              description={feature.description}
              index={index}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Features;